import { BdEquals, BdNumberFormatter } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdRangeCondition,
} from '@bindoc-projects/condition-domain-lib';

export const AYS_CLINIC_FULLY_INPATIENT_CASES_CONDITION_ID =
  'fullyInpatientCases';
export const FilterConditionFullyInpatientCasesPluginType: FilterConditionPluginSpecification<
  { from: number | null; to: number | null },
  FilterConditionFullyInpatientCasesDto,
  AysClinicFullyInpatientCasesCondition
> = {
  type: 'fullyInpatientCases',
  category: 'clinic',
  displayName: { de: 'Fallzahl' },
  sortingIndex: 8,
  occurrence: 'multiple',
  fromObject(
    obj: FilterConditionFullyInpatientCasesDto,
  ): FilterConditionPluginImpl<{ from: number | null; to: number | null }> {
    return new FilterConditionFullyInpatientCasesPlugin(obj.from, obj.to);
  },
  fromCondition(jsonObj: AysClinicFullyInpatientCasesCondition): FilterConditionPluginImpl<{
      from: number | null;
      to: number | null;
    }> {
    return new FilterConditionFullyInpatientCasesPlugin(jsonObj.from, jsonObj.to);
  },
  newInstance(): FilterConditionPluginImpl<{ from: number | null; to: number | null }> {
    return new FilterConditionFullyInpatientCasesPlugin(null, null);
  },
};

export class FilterConditionFullyInpatientCasesPlugin extends FilterConditionPluginImpl<
  { from: number | null; to: number | null },
  FilterConditionFullyInpatientCasesDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    { from: number | null; to: number | null },
    FilterConditionFullyInpatientCasesDto,
    AysClinicFullyInpatientCasesCondition
  > = FilterConditionFullyInpatientCasesPluginType;

  constructor(
    private from: number | null,
    private to: number | null,
  ) {
    super(FilterConditionFullyInpatientCasesPluginType.type);
  }

  clone(): FilterConditionFullyInpatientCasesPlugin {
    return new FilterConditionFullyInpatientCasesPlugin(this.from, this.to);
  }

  override equals(other: FilterConditionFullyInpatientCasesPlugin): boolean {
    return (
      super.equals(other) &&
      BdEquals.equals(this.from, other.from) &&
      BdEquals.equals(this.to, other.to)
    );
  }

  override valid(): boolean {
    if (Number.isInteger(this.to) && Number.isInteger(this.from)) {
      return this.from < this.to;
    } else {
      return Number.isInteger(this.to) || Number.isInteger(this.from);
    }
  }

  override validationToText(): string {
    if (Number.isInteger(this.to) && Number.isInteger(this.from)) {
      return this.from < this.to ? '' : 'Min < Max';
    } else if (Number.isInteger(this.to) || Number.isInteger(this.from)) {
      return '';
    } else {
      return 'Fälle Min oder Max auswählen';
    }
  }

  toCondition(): BdCondition | null {
    return new AysClinicFullyInpatientCasesCondition(this.from, this.to);
  }

  toJSON(): FilterConditionFullyInpatientCasesDto | null {
    return {
      type: this.type,
      from: this.from,
      to: this.to,
    };
  }

  toText(): string | null {
    if (Number.isInteger(this.to) && Number.isInteger(this.from)) {
      return `Kliniken zwischen ${BdNumberFormatter.format(this.from)} und ${BdNumberFormatter.format(this.to)} Fälle`;
    } else if (Number.isInteger(this.to)) {
      return `Kliniken bis ${BdNumberFormatter.format(this.to)} Fälle`;
    } else if (Number.isInteger(this.from)) {
      return `Kliniken ab ${BdNumberFormatter.format(this.from)} Fälle`;
    } else {
      return null;
    }
  }

  override toValue(): { from: number | null; to: number | null } {
    return {
      from: this.from,
      to: this.to,
    };
  }
}

export class AysClinicFullyInpatientCasesCondition extends BdRangeCondition<number> {
  constructor(from: number | null, to: number | null) {
    super(AYS_CLINIC_FULLY_INPATIENT_CASES_CONDITION_ID, from, to);
  }
}

interface FilterConditionFullyInpatientCasesDto {
  type: string;
  from: number | null;
  to: number | null;
}
